import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { btnPrimary } from "../ui";
import { Sparkles, Send, Loader2, User, FileText, Check, ImagePlus, Camera } from "lucide-react";

const SUGGESTIONS = ["Paciente relatou melhora no sono esta semana", "Trabalhamos técnicas de respiração; boa adesão", "Queixa de ansiedade antes de reuniões no trabalho"];
const FIELD_LABELS = { main_complaint: "queixa principal", hypothesis: "hipótese", treatment_plan: "plano terapêutico", medications: "medicações" };

export default function PatientChat({ patientId, patientName, heightClass = "h-[calc(100vh-20rem)] min-h-[420px]", widthClass = "max-w-3xl" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [readingPhoto, setReadingPhoto] = useState(false);
  const endRef = useRef(null);
  const imgRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  // Mantém a tela do celular ligada enquanto a IA lê a foto (evita perder o processamento se a tela apagar)
  useEffect(() => {
    if (!loading || !("wakeLock" in navigator)) return;
    let active = true;
    let lock = null;
    const acquire = async () => {
      try { lock = await navigator.wakeLock.request("screen"); } catch { /* sem suporte/permissão */ }
    };
    if (document.visibilityState === "visible") acquire();
    const onVis = () => { if (active && document.visibilityState === "visible" && !lock) acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVis);
      lock?.release().catch(() => {});
    };
  }, [loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/chat`, { message: msg, history });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.updated_clinical?.length) {
        const nomes = data.updated_clinical.map((k) => FIELD_LABELS[k] || k).join(", ");
        toast.success(`Anotado na evolução · dados clínicos atualizados: ${nomes}`);
      } else {
        toast.success("Anotado na evolução de hoje.");
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "Não consegui responder agora." }]);
    } finally { setLoading(false); }
  };

  const sendImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || loading) return;
    setMessages((m) => [...m, { role: "user", content: "📷 Foto de anotações enviada" }]);
    setLoading(true);
    setReadingPhoto(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/patients/${patientId}/chat-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.updated_clinical?.length) {
        const nomes = data.updated_clinical.map((k) => FIELD_LABELS[k] || k).join(", ");
        toast.success(`Foto lida e anotada · dados clínicos atualizados: ${nomes}`);
      } else {
        toast.success("Foto lida e anotada na evolução de hoje.");
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "A conexão caiu durante a leitura. Se a tela apagou no meio, a anotação pode ter sido salva mesmo assim — verifique a aba Evolução de hoje antes de reenviar a foto." }]);
    } finally { setLoading(false); setReadingPhoto(false); }
  };

  const summarize = async () => {
    if (loading) return;
    setMessages((m) => [...m, { role: "user", content: "Resumir evolução deste paciente" }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/summary`);
      setMessages((m) => [...m, { role: "assistant", content: data.summary }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "Não consegui gerar o resumo agora." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className={`flex flex-col ${heightClass} ${widthClass}`} data-testid="patient-chat">
      <button onClick={summarize} disabled={loading} data-testid="summarize-evolution" className="self-start inline-flex items-center gap-2 rounded-full bg-[#C87A5E] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#B2644D] transition-colors disabled:opacity-60">
        <FileText className="w-4 h-4" /> Resumir evolução
      </button>

      <div className="flex-1 overflow-y-auto mt-4 rounded-2xl bg-white border border-[#5B6E5B]/10 p-5 space-y-4" data-testid="patient-chat-window">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-5 py-6">
            <div className="grid place-items-center w-14 h-14 rounded-full bg-[#F5F0E6]"><Sparkles className="w-6 h-6 text-[#5B6E5B]" /></div>
            <p className="font-body text-sm text-[#7A8A7A] max-w-sm">Escreva aqui as anotações da sessão de {patientName}. Tudo que você enviar é <strong>salvo automaticamente na evolução</strong>, e a IA atualiza os <strong>dados clínicos</strong> do prontuário.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="patient-chat-suggestion" className="rounded-full border border-[#5B6E5B]/20 px-4 py-2 text-sm text-[#4A5D4E] hover:bg-[#F5F0E6] transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#5B6E5B]"><Sparkles className="w-4 h-4 text-[#FDFBF7]" /></div>}
            <div className={`max-w-[80%] flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
              <div className={`rounded-2xl px-4 py-3 font-body text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#5B6E5B] text-[#FDFBF7]" : "bg-[#F5F0E6] text-[#2C332A]"}`}>{m.content}</div>
              {m.role === "user" && (
                <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#8AA07E]"><Check className="w-3 h-3" /> salvo na evolução</span>
              )}
            </div>
            {m.role === "user" && <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#F5F0E6]"><User className="w-4 h-4 text-[#5B6E5B]" /></div>}
          </motion.div>
        ))}
        {loading && (
          <div className="flex gap-3" data-testid="chat-loading">
            <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#5B6E5B]"><Sparkles className="w-4 h-4 text-[#FDFBF7]" /></div>
            <div className="bg-[#F5F0E6] rounded-2xl px-4 py-3 flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin text-[#5B6E5B]" />
              {readingPhoto && <p className="font-body text-xs text-[#4A5D4E]" data-testid="chat-reading-photo">Lendo a foto… pode levar até 1 minutinho. Deixe esta tela aberta — ela vai ficar ligada sozinha.</p>}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2 sm:gap-3 items-center" data-testid="patient-chat-form">
        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid="patient-chat-image-input" />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={sendImage} data-testid="patient-chat-camera-input" />
        <button type="button" onClick={() => imgRef.current?.click()} disabled={loading} title="Escolher foto da galeria para a IA ler"
          data-testid="patient-chat-image-btn"
          className="shrink-0 grid place-items-center w-11 h-11 rounded-full bg-white border border-[#5B6E5B]/20 text-[#5B6E5B] hover:bg-[#F5F0E6] transition-colors disabled:opacity-60">
          <ImagePlus className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => camRef.current?.click()} disabled={loading} title="Tirar foto agora para a IA ler"
          data-testid="patient-chat-camera-btn"
          className="shrink-0 grid place-items-center w-11 h-11 rounded-full bg-white border border-[#5B6E5B]/20 text-[#5B6E5B] hover:bg-[#F5F0E6] transition-colors disabled:opacity-60">
          <Camera className="w-5 h-5" />
        </button>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Digite a anotação da sessão..." data-testid="patient-chat-input"
          className="flex-1 rounded-full bg-white border border-[#5B6E5B]/15 px-5 py-3 font-body text-sm text-[#2C332A] outline-none focus:border-[#C87A5E] focus:ring-2 focus:ring-[#C87A5E]/25" />
        <button type="submit" disabled={loading} className={btnPrimary} data-testid="patient-chat-send">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
      <p className="font-body text-[11px] text-[#A8B0A2] mt-2">Digite ou envie uma <strong>foto da galeria</strong> ou <strong>tire na hora</strong> (botão de câmera) — a IA lê, salva na evolução do dia e atualiza os dados clínicos. Revise no prontuário. Não substitui avaliação clínica.</p>
    </div>
  );
}
